import { useCallback, useEffect, useState } from 'react';
import type { Court, PlayEvent } from '@court-sync/shared';
import { clubService } from '@/services/club';
import { occupancyService } from '@/services/occupancy';
import { courtHireService } from '@/services/courtHire';
import { usePlayEventCalendarStore } from '@/store/playEventCalendar';
import {
  buildClosureOccurrencesFromOccupancy,
  buildCourtHireOccurrencesFromBookings,
  buildLessonOccurrencesFromOccupancy,
  buildPlayEventOccurrencesFromEvents,
  buildPlayEventOccurrencesFromOccupancy,
  mergePlayEventOccurrences,
  type ScheduleOccurrence,
} from '@/utils/scheduleOccurrences';

export interface UseFacilityScheduleLayerOptions {
  clubId: string | undefined;
  rangeStart: Date;
  rangeEnd: Date;
  includeCourtHire: boolean;
  includeClosures: boolean;
  /** Club-only operators: lessons from occupancy. Academy operators must keep this false. */
  includeOccupancyLessons: boolean;
  /** Match play events for the org calendar layer. */
  includePlayEvents?: boolean;
  organizationId?: string;
  enabled?: boolean;
}

export interface FacilityScheduleLayerResult {
  occupancyLessons: ScheduleOccurrence[];
  courtHireOccurrences: ScheduleOccurrence[];
  closureOccurrences: ScheduleOccurrence[];
  playEventOccurrences: ScheduleOccurrence[];
  facilityOccurrences: ScheduleOccurrence[];
  clubCourts: Court[];
  loading: boolean;
  error: string | null;
  refresh: (options?: { forcePlayEvents?: boolean }) => Promise<void>;
}

export function useFacilityScheduleLayer(
  options: UseFacilityScheduleLayerOptions
): FacilityScheduleLayerResult {
  const {
    clubId,
    rangeStart,
    rangeEnd,
    includeCourtHire,
    includeClosures,
    includeOccupancyLessons,
    includePlayEvents = false,
    organizationId,
    enabled = true,
  } = options;

  const [occupancyLessons, setOccupancyLessons] = useState<ScheduleOccurrence[]>([]);
  const [courtHireOccurrences, setCourtHireOccurrences] = useState<ScheduleOccurrence[]>([]);
  const [closureOccurrences, setClosureOccurrences] = useState<ScheduleOccurrence[]>([]);
  const [playEventOccurrences, setPlayEventOccurrences] = useState<ScheduleOccurrence[]>([]);
  const [clubCourts, setClubCourts] = useState<Court[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const needsOccupancy =
    includeClosures || includeOccupancyLessons || (includePlayEvents && !!clubId);
  const needsPlayEvents = includePlayEvents && !!organizationId;
  const needsFetch =
    enabled &&
    (clubId || needsPlayEvents) &&
    (needsOccupancy || includeCourtHire || needsPlayEvents);

  const load = useCallback(async (options?: { forcePlayEvents?: boolean }) => {
    if (!needsFetch) {
      setOccupancyLessons([]);
      setCourtHireOccurrences([]);
      setClosureOccurrences([]);
      setPlayEventOccurrences([]);
      setClubCourts([]);
      setError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const from = rangeStart.toISOString();
      const to = rangeEnd.toISOString();

      const courtsPromise = clubId
        ? clubService.getClubCourts(clubId)
        : Promise.resolve({ success: true as const, data: [] as Court[] });
      const occupancyPromise =
        clubId && needsOccupancy
          ? occupancyService.getCalendar(clubId, from, to)
          : Promise.resolve({ success: true, data: [] });
      const bookingsPromise =
        clubId && includeCourtHire
          ? courtHireService.getBookings(clubId, { from, to })
          : Promise.resolve({ success: true, data: [] });
      const playEventsPromise: Promise<PlayEvent[]> =
        needsPlayEvents && organizationId
          ? usePlayEventCalendarStore
              .getState()
              .fetchForRange(organizationId, rangeStart, rangeEnd, {
                force: options?.forcePlayEvents,
              })
          : Promise.resolve([]);

      const [courtsRes, occupancyRes, bookingsRes, playEvents] = await Promise.all([
        courtsPromise,
        occupancyPromise,
        bookingsPromise,
        playEventsPromise,
      ]);

      const courtList =
        courtsRes.success && courtsRes.data
          ? courtsRes.data.filter((c) => c.isActive)
          : [];
      setClubCourts(courtList);

      const namesById: Record<string, string> = {};
      for (const court of courtList) {
        namesById[court.id] = court.name;
      }

      const occupancyItems =
        occupancyRes.success && occupancyRes.data ? occupancyRes.data : [];

      const lessons = includeOccupancyLessons
        ? buildLessonOccurrencesFromOccupancy(occupancyItems, namesById)
        : [];
      setOccupancyLessons(lessons);

      const closures = includeClosures
        ? buildClosureOccurrencesFromOccupancy(occupancyItems, namesById)
        : [];
      setClosureOccurrences(closures);

      const bookings =
        bookingsRes.success && bookingsRes.data ? bookingsRes.data : [];
      const hireOccurrences = includeCourtHire
        ? buildCourtHireOccurrencesFromBookings(bookings, namesById)
        : [];
      setCourtHireOccurrences(hireOccurrences);

      const eventNamesById = Object.fromEntries(playEvents.map((event) => [event.id, event.name]));
      const playFromEvents = needsPlayEvents
        ? buildPlayEventOccurrencesFromEvents(playEvents, namesById)
        : [];
      const playFromOccupancy =
        needsPlayEvents && occupancyItems.length > 0
          ? buildPlayEventOccurrencesFromOccupancy(
              occupancyItems,
              namesById,
              eventNamesById
            )
          : [];
      const playOccurrences = needsPlayEvents
        ? mergePlayEventOccurrences(playFromEvents, playFromOccupancy)
        : [];
      setPlayEventOccurrences(playOccurrences);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load facility schedule');
      setOccupancyLessons([]);
      setCourtHireOccurrences([]);
      setClosureOccurrences([]);
      setPlayEventOccurrences([]);
      setClubCourts([]);
    } finally {
      setLoading(false);
    }
  }, [
    clubId,
    organizationId,
    rangeStart,
    rangeEnd,
    includeCourtHire,
    includeClosures,
    includeOccupancyLessons,
    includePlayEvents,
    needsFetch,
    needsOccupancy,
    needsPlayEvents,
  ]);

  useEffect(() => {
    void load();
  }, [load]);

  const facilityOccurrences = [
    ...occupancyLessons,
    ...courtHireOccurrences,
    ...closureOccurrences,
    ...playEventOccurrences,
  ];

  return {
    occupancyLessons,
    courtHireOccurrences,
    closureOccurrences,
    playEventOccurrences,
    facilityOccurrences,
    clubCourts,
    loading,
    error,
    refresh: () => load({ forcePlayEvents: true }),
  };
}
